const MEBIBYTE = 1024 * 1024;

export const MAX_PART_IMAGES = 6;
export const MAX_PART_IMAGE_BYTES = 50 * MEBIBYTE;
export const MAX_TOTAL_PART_IMAGE_BYTES = 200 * MEBIBYTE;
export const MAX_PART_IMAGE_PIXELS = 40_000_000;
export const ACCEPTED_PART_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
};
const HEIF_EXTENSIONS = new Set([
  ...ACCEPTED_PART_IMAGE_TYPES['image/heic'],
  ...ACCEPTED_PART_IMAGE_TYPES['image/heif'],
]);
const ACCEPTED_EXTENSIONS = new Set(Object.values(ACCEPTED_PART_IMAGE_TYPES).flat());

export interface PartImageSelection {
  files: File[];
  problem?: string;
}

const extensionOf = (file: File): string => file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

const isAcceptedType = (file: File): boolean =>
  file.type ? Object.keys(ACCEPTED_PART_IMAGE_TYPES).includes(file.type) : ACCEPTED_EXTENSIONS.has(extensionOf(file));

export const isHeifFile = (file: File): boolean =>
  file.type === 'image/heic' || file.type === 'image/heif' || HEIF_EXTENSIONS.has(extensionOf(file));

const totalBytes = (files: File[]): number => files.reduce((sum, file) => sum + file.size, 0);

export function addPartImageFiles(current: File[], added: File[]): PartImageSelection {
  const valid = added.filter(file => isAcceptedType(file) && file.size <= MAX_PART_IMAGE_BYTES);
  const combined = [...current, ...valid];
  const files = combined.slice(0, MAX_PART_IMAGES);
  if (totalBytes(files) > MAX_TOTAL_PART_IMAGE_BYTES) {
    return { files: current, problem: 'Photos can total 200 MB at most.' };
  }
  if (valid.length < added.length) {
    return { files, problem: 'Only JPEG, PNG, WebP, or HEIC photos up to 50 MB each are accepted.' };
  }
  if (combined.length > MAX_PART_IMAGES) {
    return { files, problem: `Use up to ${MAX_PART_IMAGES} photos.` };
  }
  return { files };
}
